import type { DrawingText } from './drawingGeometry';

/**
 * Reads the loading off a truss design drawing, such as MiTek's: the LOADING block (TCLL 20.0, Lumber DOL 1.15, Code
 * IBC2018/TPI2014, the L/d limits) and the notes that set wind, snow, and other loads ("Wind: ASCE 7-16; Vult=115mph
 * ... Exp B; Enclosed"). It turns them into the app's load and analysis settings, so the Assistant can give a truss
 * the drawing's loading in one change per kind instead of reading each value and guessing how the app spells it.
 */

export interface DrawingLoads {
  /** What the drawing says, in its own words, for the designer to check against. */
  readonly read: readonly string[];
  /** The load settings that match, nested as change_truss_settings takes them. */
  readonly load: Readonly<Record<string, unknown>>;
  /** The analysis settings that match. */
  readonly analysis: Readonly<Record<string, unknown>>;
  /** Spacing in inches, which is the truss's own, not a setting. */
  readonly spacing: number | null;
  /** What the drawing says that no setting above covers. */
  readonly notSet: readonly string[];
}

const NUMBER = String.raw`(\d+(?:\.\d+)?)`;

/** Lines of text, top to bottom, each read left to right. */
function readLines(texts: readonly DrawingText[]) {
  const rows: { y: number; texts: DrawingText[] }[] = [];
  for (const text of [...texts].sort((a, b) => b.y - a.y)) {
    const row = rows.find((candidate) => Math.abs(candidate.y - text.y) <= 2);
    if (row != null) row.texts.push(text);
    else rows.push({ y: text.y, texts: [text] });
  }
  return rows.map((row) =>
    row.texts
      .sort((a, b) => a.x - b.x)
      .map((text) => text.text.trim())
      .join(' '),
  );
}

/** The text of the value printed under a title-block heading such as "Truss Type". */
function readTitleBlock(texts: readonly DrawingText[], heading: string) {
  const label = texts.find((text) => text.text.trim() === heading);
  if (label == null) return null;
  const below = texts
    .filter((text) => Math.abs(text.x - label.x) < 3 && text.y < label.y && label.y - text.y < 20)
    .sort((a, b) => b.y - a.y);
  return below[0]?.text.trim() ?? null;
}

function parseFeetInchesSixteenths(text: string) {
  const match = /^(\d+)-(\d+)-(\d+)$/.exec(text);
  return match == null ? null : Number(match[1]) * 12 + Number(match[2]) + Number(match[3]) / 16;
}

/** "IBC2018" as the app spells it, "Ibc2018". */
const toEnumSpelling = (code: string) => code.charAt(0) + code.slice(1).toLowerCase();

const TERRAIN = { B: 'B_SuburbanAreas', C: 'C_ScatteredObstructions', D: 'D_FlatOpenTerrain' } as const;
const ENCLOSURE = {
  Enclosed: 'Enclosed',
  'Partially Enclosed': 'PartiallyEnclosed',
  'Partially Open': 'PartiallyOpen',
  Open: 'Open',
} as const;
const SNOW_EXPOSURE = { Fully: 'FullyExposed', Partially: 'PartiallyExposed', Sheltered: 'Sheltered' } as const;

/** Sides a note names, as in "cantilever left and right exposed". */
function exposedSides(note: string, what: string) {
  const match = new RegExp(`${what} (left and right|left|right) exposed`).exec(note);
  if (match == null) return null;
  return { left: match[1].includes('left'), right: match[1].includes('right') };
}

/**
 * Reads the loading from a drawing page's text, or returns `null` if the page has no LOADING block.
 */
export function readDrawingLoads(texts: readonly DrawingText[]): DrawingLoads | null {
  const lines = readLines(texts);
  const all = lines.join(' ').replace(/\s+/g, ' ');
  if (!/\bLOADING\b/.test(all) || !/\bTCLL\b/.test(all)) return null;

  const value = (pattern: RegExp) => pattern.exec(all)?.[1] ?? null;
  const numberAfter = (label: string) => {
    const text = value(new RegExp(`\\b${label}\\s+${NUMBER}`));
    return text == null ? null : Number(text);
  };

  // The notes, each starting "1)", "2)"; a note can wrap onto several lines.
  const notesStart = all.search(/\bNOTES-/);
  const notes =
    notesStart < 0
      ? []
      : all
          .slice(notesStart + 'NOTES-'.length)
          .split(/\s(?=\d{1,2}\)\s)/)
          .map((note) => note.trim())
          // Some drawings break words where the font changes: "Wi nd", "MW FRS".
          .map((note) => note.replace(/\bWi nd:/g, 'Wind:').replace(/\bMW FRS\b/g, 'MWFRS'));
  const note = (pattern: RegExp) => notes.find((candidate) => pattern.test(candidate)) ?? null;

  const read: string[] = [];
  const load: Record<string, unknown> = {};
  const analysis: Record<string, unknown> = {};
  const notSet: string[] = [];

  const trussType = readTitleBlock(texts, 'Truss Type');
  const lumberDol = numberAfter('Lumber DOL');
  const isFloor = /FLOOR/i.test(trussType ?? '') || lumberDol === 1;
  const prefix = isFloor ? 'floor' : 'roof';
  if (trussType != null) read.push(`Truss type ${trussType}`);

  const tcll = numberAfter('TCLL');
  const tcdl = numberAfter('TCDL');
  const bcll = numberAfter('BCLL');
  const bcdl = numberAfter('BCDL');
  read.push(`TCLL ${tcll}, TCDL ${tcdl}, BCLL ${bcll}, BCDL ${bcdl} psf`);
  if (tcll != null) load[`${prefix}TopChordLiveLoad`] = tcll;
  if (tcdl != null) load[`${prefix}TopChordDeadLoad`] = tcdl;
  if (bcll != null) load[`${prefix}BottomChordLiveLoad`] = bcll;
  if (bcdl != null) load[`${prefix}BottomChordDeadLoad`] = bcdl;

  // A bottom chord live load that the BCLL leaves out, applied on its own: "a 10.0 psf bottom chord live load
  // nonconcurrent with any other live loads".
  const nonconcurrent = note(/bottom chord live load nonconcurrent/);
  const nonconcurrentLoad =
    nonconcurrent && /(\d+(?:\.\d+)?) ?psf bottom chord live load nonconcurrent/.exec(nonconcurrent);
  if (nonconcurrentLoad != null && !isFloor) {
    read.push(`${nonconcurrentLoad[1]} psf bottom chord live load nonconcurrent with other live loads`);
    load.roofBottomChordLiveLoad = Number(nonconcurrentLoad[1]);
    load.applyBottomChordLiveLoadNonConcurrently = true;
  }

  const rectangle = note(/live load of [\d.]+ ?psf on the bottom chord in all areas where a rectangle/);
  if (rectangle != null)
    notSet.push(
      `The BCLL's *: "${rectangle.replace(/^\d+\)\s*/, '').replace(/^\*\s*/, '')}" Ask the designer how to load it.`,
    );

  if (note(/Unbalanced roof live loads have been considered/) != null) {
    read.push('Unbalanced roof live loads considered');
    load.considerUnbalancedRoofLiveLoad = true;
  }
  if (note(/Unbalanced floor live loads have been considered/) != null) {
    read.push('Unbalanced floor live loads considered');
    load.considerUnbalancedFloorLiveLoad = true;
  }

  const code = /\bCode\s+([A-Z]+\d{4})\/([A-Z]+\d{4})/.exec(all);
  if (code != null) {
    read.push(`Code ${code[1]}/${code[2]}`);
    load.buildingCode = toEnumSpelling(code[1]);
    analysis.designStandard = toEnumSpelling(code[2]);
  }

  const plateGripDol = numberAfter('Plate Grip DOL');
  const repetitive = value(/Rep Stress Incr\s+(YES|NO)\b/);
  read.push(`Lumber DOL ${lumberDol}, Plate Grip DOL ${plateGripDol}, Rep Stress Incr ${repetitive}`);
  if (repetitive != null) analysis.useRepetitiveMemberIncrease = repetitive === 'YES';

  // The L/d column: Vert(LL) is live load deflection, Vert(CT) or Vert(TL) total.
  const liveLimit = value(/Vert\(LL\)\s+-?[\d.]+\s+\S+\s+\S+\s+(\d+)\b/);
  const totalLimit = value(/Vert\((?:CT|TL)\)\s+-?[\d.]+\s+\S+\s+\S+\s+(\d+)\b/);
  if (liveLimit != null || totalLimit != null) {
    read.push(`Deflection limits L/${liveLimit} live, L/${totalLimit} total`);
    analysis[`${prefix}DeflectionLimits`] = {
      ...(liveLimit != null ? { liveTrussSpanToDeflectionRatio: Number(liveLimit) } : {}),
      ...(totalLimit != null ? { totalTrussSpanToDeflectionRatio: Number(totalLimit) } : {}),
    };
  }

  const spacingText = value(/SPACING-?\s+(\d+-\d+-\d+)/);
  const spacing = spacingText == null ? null : parseFeetInchesSixteenths(spacingText);
  if (spacingText != null) read.push(`Spacing ${spacingText}`);

  const snow = note(/\bPg=/);
  if (snow != null) {
    const asce = /ASCE 7-(\d+)/.exec(snow)?.[1];
    const pg = /\bPg=\s*(\d+(?:\.\d+)?)/.exec(snow)?.[1];
    const exposure = /\b(Fully|Partially) Exp|\b(Sheltered)\b/.exec(snow);
    const snowDol = /\bPf=[^(]*\(Lum DOL=\s*(\d+(?:\.\d+)?)/.exec(snow)?.[1];
    const roofLiveDol = /roof LL: Lum DOL=\s*(\d+(?:\.\d+)?)/.exec(snow)?.[1];
    read.push(`Snow: ${snow.replace(/^\d+\)\s*/, '')}`);
    load.useSnow = true;
    if (pg != null) {
      load.snowLoad = Number(pg);
      // ASCE 7-22 gives the ground snow load at strength level; earlier editions at allowable stress.
      load.activeSnowLoad = asce != null && Number(asce) >= 22 ? 'UltimateGround' : 'AsdGround';
    }
    const exposureWord = (exposure?.[1] ?? exposure?.[2]) as keyof typeof SNOW_EXPOSURE | undefined;
    if (exposureWord != null) load.exposure = SNOW_EXPOSURE[exposureWord];
    if (snowDol != null) load.snowDurationFactor = Number(snowDol);
    if (roofLiveDol != null) load.roofLiveDurationFactor = Number(roofLiveDol);
    if (note(/Unbalanced snow loads have been considered/) != null) load.considerUnbalancedSnow = true;
    const ct = /\bCt=\s*([\d.]+)/.exec(snow)?.[1];
    if (ct != null) notSet.push(`Snow Ct=${ct}: set thermalCondition to match it if it isn't the default.`);
  } else if (!isFloor) {
    load.useSnow = false;
  }

  // The drawing's Lumber DOL is what it applied to its governing live load: floor live, or roof live without snow.
  if (lumberDol != null) {
    if (isFloor) load.floorLiveDurationFactor = lumberDol;
    else if (snow == null) load.roofLiveDurationFactor = lumberDol;
  }

  const wind = note(/\bWind:/);
  if (wind != null) {
    read.push(`Wind: ${wind.replace(/^\d+\)\s*/, '').replace(/^Wind:\s*/, '')}`);
    load.useWind = true;
    const speed = /Vult=\s*(\d+(?:\.\d+)?)\s*mph/.exec(wind)?.[1];
    if (speed != null) load.windSpeed = Number(speed);
    const category = /Cat\.\s*(IV|I{1,3})\b/.exec(wind)?.[1];
    if (category != null) load.riskCategory = category;
    const exposure = /Exp\s*([BCD])\b/.exec(wind)?.[1] as keyof typeof TERRAIN | undefined;
    if (exposure != null) load.terrainExposureCategory = TERRAIN[exposure];
    const enclosure = /Exp\s*[BCD];\s*(Partially Enclosed|Partially Open|Enclosed|Open)\b/.exec(wind)?.[1] as
      keyof typeof ENCLOSURE | undefined;
    if (enclosure != null) load.enclosureClassification = ENCLOSURE[enclosure];
    // Heights and the building's size are in feet, as the settings keep them.
    const feet = (name: string) => new RegExp(`\\b${name}=\\s*${NUMBER}\\s*ft`).exec(wind)?.[1];
    for (const [name, key] of [
      ['h', 'meanRoofHeight'],
      ['B', 'buildingWidth'],
      ['L', 'buildingLength'],
    ] as const) {
      const size = feet(name);
      if (size != null) load[key] = Number(size);
    }
    // The dead loads the wind uplift is resisted by, which are less than the full dead loads.
    const upliftTcdl = new RegExp(`\\bTCDL=\\s*${NUMBER}\\s*psf`).exec(wind)?.[1];
    if (upliftTcdl != null) load.maxTopChordDeadLoadForUpliftOverride = Number(upliftTcdl);
    const upliftBcdl = new RegExp(`\\bBCDL=\\s*${NUMBER}\\s*psf`).exec(wind)?.[1];
    if (upliftBcdl != null) load.maxBottomChordDeadLoadForUpliftOverride = Number(upliftBcdl);

    // "MWFRS (directional) and C-C ... C-C for members and forces & MWFRS for reactions" is the hybrid method.
    const mwfrs = /MWFRS\s*\((directional|envelope)\)/.exec(wind)?.[1];
    const componentsAndCladding = /\bC-C\b/.test(wind);
    const method = mwfrs === 'envelope' ? 'Envelope' : 'Directional';
    if (mwfrs != null)
      load.windDesignMethod = componentsAndCladding ? `WindAsceHybrid${method}` : `WindAsceMwfrs${method}`;
    else if (componentsAndCladding) load.windDesignMethod = 'WindAsceCc';

    for (const [what, key] of [
      ['cantilever', 'CantileverExposed'],
      ['end vertical', 'EndVerticalExposed'],
      ['porch', 'PorchExposed'],
    ] as const) {
      const sides = exposedSides(wind, what);
      if (sides == null) continue;
      if (sides.left) load[`isLeft${key}`] = true;
      if (sides.right) load[`isRight${key}`] = true;
    }

    const windDol = new RegExp(`Lumber DOL=\\s*${NUMBER}`).exec(wind)?.[1];
    if (windDol != null) load.windDurationFactor = Number(windDol);
  } else if (!isFloor) {
    read.push('No wind note: the truss wasn’t designed for wind');
    load.useWind = false;
  }

  return { read, load, analysis, spacing, notSet };
}

/** The loading as the model reads it, with the settings ready to give the truss. */
export function describeDrawingLoads(loads: DrawingLoads) {
  return [
    'Loading read from the drawing’s LOADING block and notes:',
    ...loads.read.map((line) => `- ${line}`),
    'As settings, ready for change_truss_settings (one call per kind, with these changes as given):',
    `- load: ${JSON.stringify(loads.load)}`,
    ...(Object.keys(loads.analysis).length > 0 ? [`- analysis: ${JSON.stringify(loads.analysis)}`] : []),
    ...(loads.spacing != null ? [`Spacing is ${loads.spacing} inches; set it with set_plies_and_spacing.`] : []),
    ...(loads.notSet.length > 0 ? ['Not covered by those settings:', ...loads.notSet.map((line) => `- ${line}`)] : []),
  ].join('\n');
}
